import type {
  EngagementTypeDef,
  InteractionType,
  ScenarioOption,
} from './expectedAction.interface.js';

/**
 * Key under which an engagement entry's free-form reasoning is stored, matching
 * the reference Labeling Studio convention.
 */
export const REASONING_INPUT_KEY = '_reasoning';

/**
 * Static engagement-type registry, transcribed verbatim from the reference
 * Labeling Studio's `GET /api/engagement-types` response (backed there by
 * static JSON, no external service). Drives the "Engagement Types" checklist.
 */
export const ENGAGEMENT_TYPES: readonly EngagementTypeDef[] = [
  {
    id: 'channel_message_broadcast',
    name: 'Channel Message Broadcast',
    description: 'The agent posts a message to a Teams channel',
    category: 'Messaging',
    inputs: [
      {
        name: 'channel_name',
        type: 'freetext',
        description: 'The name of the Teams channel to post in',
      },
      {
        name: 'message',
        type: 'freetext',
        description: 'The message content to post in the channel',
      },
    ],
  },
  {
    id: 'commands_and_meeting_control',
    name: 'Commands and Meeting Control',
    description: 'The agent executes commands or controls meeting functions',
    category: 'Meeting Controls',
    inputs: [
      {
        name: 'command',
        type: 'enum',
        description: 'The meeting control command to execute',
        options: [
          'start_recording',
          'stop_recording',
          'start_transcription',
          'stop_transcription',
          'mute_participant',
          'unmute_participant',
          'admit_participant',
        ],
      },
    ],
  },
  {
    id: 'hand_raise',
    name: 'Hand raise',
    description: 'The agent raise hand to get speaking permission',
    category: 'Voice actions',
    inputs: [
      {
        name: 'message',
        type: 'freetext',
        description: 'What the agent will say once it gets speaking permission',
      },
    ],
  },
  {
    id: 'live_notes',
    name: 'Live Notes',
    description: 'The agent generates or updates live notes during a meeting',
    category: 'Content Generation',
    inputs: [
      { name: 'content', type: 'freetext', description: 'The notes content' },
    ],
  },
  {
    id: 'meeting_chat_message',
    name: 'Meeting Chat Message',
    description: 'The agent sends a message in the meeting chat',
    category: 'Messaging',
    inputs: [
      {
        name: 'message',
        type: 'freetext',
        description: 'The message content to send in the meeting chat',
      },
    ],
  },
  {
    id: 'other',
    name: 'Other',
    description:
      'A custom engagement type not covered by the predefined categories',
    category: 'Uncategorized',
    inputs: [
      {
        name: 'description',
        type: 'freetext',
        description: 'Describe the engagement',
      },
    ],
  },
  {
    id: 'private_chat_message',
    name: 'Private Chat Message',
    description: 'The agent sends a private chat message to a user',
    category: 'Messaging',
    inputs: [
      {
        name: 'recipient',
        type: 'freetext',
        description: 'The user to send the private message to',
      },
      {
        name: 'message',
        type: 'freetext',
        description: 'The message content to send privately',
      },
    ],
  },
  {
    id: 'reply_with_voice',
    name: 'Reply with voice',
    description: 'The agent should reply with voice',
    category: 'Voice actions',
    inputs: [
      {
        name: 'Speech',
        type: 'freetext',
        description: 'What the agent should say',
      },
    ],
  },
  {
    id: 'run_poll',
    name: 'Run Poll',
    description: 'runs a poll',
    category: 'Commands',
    inputs: [
      {
        name: 'options',
        type: 'freetext',
        description: 'comma separated options',
      },
    ],
  },
];

/** The two interaction-type tags an expected action can carry. */
export const INTERACTION_TYPES: readonly InteractionType[] = [
  'reactive',
  'proactive',
];

/**
 * Static scenario library, transcribed from the reference Labeling Studio's
 * scenario JSON files. Feeds the optional "Scenarios" picker.
 */
export const SCENARIO_LIBRARY: readonly ScenarioOption[] = [
  {
    id: 'active_customizations_check',
    name: 'Active Customizations Check',
    type: 'reactive',
  },
  { id: 'ado_board_update', name: 'ADO Board Update', type: 'reactive' },
  {
    id: 'agenda_auto_ingestion',
    name: 'Agenda Auto-Ingestion',
    type: 'proactive',
  },
  {
    id: 'agenda_crowd_sourcing',
    name: 'Agenda Crowd Sourcing',
    type: 'reactive',
  },
  { id: 'agenda_display', name: 'Agenda Display', type: 'proactive' },
  { id: 'agenda_nudge', name: 'Agenda Nudge', type: 'proactive' },
  { id: 'assumption_tracking', name: 'Assumption Tracking', type: 'proactive' },
  {
    id: 'at_mention_absent_participant',
    name: 'At-Mention Absent Participant',
    type: 'reactive',
  },
  { id: 'av_tech_support', name: 'AV Tech Support', type: 'proactive' },
  {
    id: 'availability_conflict',
    name: 'Availability Conflict',
    type: 'proactive',
  },
  { id: 'blocker_detection', name: 'Blocker Detection', type: 'proactive' },
  {
    id: 'brainstorm_facilitation',
    name: 'Brainstorm Facilitation',
    type: 'reactive',
  },
  { id: 'breakout_rooms', name: 'Breakout Rooms', type: 'reactive' },
  {
    id: 'channel_message_broadcast',
    name: 'Channel Message Broadcast',
    type: 'reactive',
  },
  {
    id: 'chat_history_reference',
    name: 'Chat History Context',
    type: 'reactive',
  },
  {
    id: 'co_creation_ideation',
    name: 'Co-Creation Ideation',
    type: 'reactive',
  },
  { id: 'commanding', name: 'Commanding', type: 'reactive' },
  {
    id: 'consensus_objection_tracking',
    name: 'Consensus Objection Tracking',
    type: 'reactive',
  },
  {
    id: 'content_triggered_action',
    name: 'Content-Triggered Action',
    type: 'reactive',
  },
  { id: 'contextual_refusal', name: 'Contextual Refusal', type: 'reactive' },
  {
    id: 'conversation_mode_engagement',
    name: 'Conversation Mode Engagement',
    type: 'proactive',
  },
  { id: 'conversation_tone', name: 'Conversation Tone', type: 'reactive' },
  {
    id: 'conversation_tracking',
    name: 'Conversation Tracking',
    type: 'proactive',
  },
  {
    id: 'decision_rationale_recall',
    name: 'Decision Rationale Recall',
    type: 'reactive',
  },
  {
    id: 'decision_readiness_check',
    name: 'Decision Readiness Check',
    type: 'proactive',
  },
  {
    id: 'document_reference',
    name: 'Document Reference During Meeting',
    type: 'reactive',
  },
  {
    id: 'enterprise_knowledge_lookup',
    name: 'Enterprise Knowledge Lookup',
    type: 'reactive',
  },
  { id: 'factual_correction', name: 'Factual Correction', type: 'proactive' },
  { id: 'follow_up_email', name: 'Follow-Up Email', type: 'reactive' },
  { id: 'goal_detection', name: 'Goal Detection', type: 'proactive' },
  {
    id: 'governance_risk_flag',
    name: 'Governance Risk Flag',
    type: 'proactive',
  },
  { id: 'late_joiner_catchup', name: 'Late Joiner Catchup', type: 'reactive' },
  {
    id: 'meeting_extension_awareness',
    name: 'Meeting Extension Awareness',
    type: 'proactive',
  },
  {
    id: 'meeting_metadata_questions',
    name: 'Meeting Metadata Questions',
    type: 'reactive',
  },
  { id: 'meeting_notes', name: 'Meeting Notes', type: 'reactive' },
  { id: 'meeting_reminder', name: 'Meeting Reminder', type: 'reactive' },
  { id: 'meeting_timer', name: 'Meeting Timer', type: 'reactive' },
  {
    id: 'meeting_transcript_qna',
    name: 'Meeting Transcript Questions',
    type: 'reactive',
  },
  { id: 'mid_point_reminder', name: 'Mid-Point Reminder', type: 'proactive' },
  { id: 'missing_role_alert', name: 'Missing Role Alert', type: 'proactive' },
  {
    id: 'multi_turn_follow_up',
    name: 'Multi-Turn Follow-Up',
    type: 'reactive',
  },
  { id: 'outcome_capture', name: 'Outcome Capture', type: 'proactive' },
  { id: 'overtime_alert', name: 'Overtime Alert', type: 'proactive' },
  { id: 'poll_creation', name: 'Poll Creation', type: 'reactive' },
  { id: 'presentation_pacing', name: 'Presentation Pacing', type: 'proactive' },
  {
    id: 'principles_alignment_check',
    name: 'Principles Alignment Check',
    type: 'proactive',
  },
  {
    id: 'prior_decision_recall',
    name: 'Prior Decision Recall',
    type: 'proactive',
  },
  {
    id: 'proactive_action_item_detection',
    name: 'Proactive Action Item Detection',
    type: 'proactive',
  },
  {
    id: 'proactive_captions_suggestion',
    name: 'Proactive Captions Suggestion',
    type: 'proactive',
  },
  {
    id: 'proactive_knowledge_gap_offer',
    name: 'Proactive Knowledge-Gap Offer',
    type: 'proactive',
  },
  {
    id: 'proactive_recording_suggestion',
    name: 'Proactive Recording Suggestion',
    type: 'proactive',
  },
  {
    id: 'problem_framing_nudge',
    name: 'Problem Framing Nudge',
    type: 'proactive',
  },
  { id: 'quorum_check', name: 'Quorum Check', type: 'reactive' },
  {
    id: 'redundant_work_prevention',
    name: 'Redundant Work Prevention',
    type: 'proactive',
  },
  { id: 'routine_management', name: 'Routine Management', type: 'reactive' },
  { id: 'run_of_show', name: 'Run of Show', type: 'reactive' },
  {
    id: 'scrum_proactive_summary',
    name: 'Scrum Proactive Summary',
    type: 'proactive',
  },
  {
    id: 'scrum_workitem_tracking',
    name: 'Scrum Work Item Tracking',
    type: 'reactive',
  },
  {
    id: 'sensitive_concern_escalation',
    name: 'Sensitive Concern Escalation',
    type: 'proactive',
  },
  {
    id: 'status_report_generation',
    name: 'Status Report Generation',
    type: 'reactive',
  },
  {
    id: 'task_completion_assistance',
    name: 'Task Completion Assistance',
    type: 'proactive',
  },
  { id: 'task_scheduling', name: 'Task Scheduling', type: 'reactive' },
  { id: 'task_status_query', name: 'Task Status Query', type: 'reactive' },
  {
    id: 'team_memory_channel_rules',
    name: 'Team Memory Channel Rules',
    type: 'reactive',
  },
  {
    id: 'time_based_moderation',
    name: 'Time-Based Moderation',
    type: 'proactive',
  },
  {
    id: 'topic_circling_detection',
    name: 'Topic Circling Detection',
    type: 'proactive',
  },
  {
    id: 'topic_completion_tracking',
    name: 'Topic Completion Tracking',
    type: 'proactive',
  },
  { id: 'typo_correction', name: 'Typo Correction', type: 'reactive' },
  {
    id: 'user_preference_persistence',
    name: 'User Preference Persistence',
    type: 'reactive',
  },
  {
    id: 'web_search_in_meeting',
    name: 'Web Search During Discussion',
    type: 'reactive',
  },
  { id: 'welcome_message', name: 'Welcome Message', type: 'proactive' },
  {
    id: 'whiteboard_visualization',
    name: 'Whiteboard Visualization',
    type: 'reactive',
  },
  { id: 'work_back_plan', name: 'Work-Back Plan Creation', type: 'reactive' },
  {
    id: 'workload_reality_check',
    name: 'Workload Reality Check',
    type: 'proactive',
  },
  { id: 'wrap_up_alert', name: 'Wrap-Up Alert', type: 'proactive' },
];
